import { useEffect, useMemo, useRef, useState } from 'react';

import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@/tsClient';

type DataSetWithVersion = {
  data: unknown;
  version: number | undefined;
};

type Section = {
  fields?: Array<{
    autofill: {
      expression: {
        variables: Array<{
          referenceType: string;
          referenceId: string;
        }>;
      } | null;
    } | null;
  }>;
};

type Props = {
  sections: Section[];
  additionalSlugs?: string[];
};

const useExternalDataSets = ({ sections, additionalSlugs = [] }: Props): Record<string, unknown> => {
  const [datasets, setDatasets] = useState<Record<string, DataSetWithVersion>>({});
  const { getData: getDatasets } = useNoodleApi(tsClient.datasets.getDatasets);
  const isLoadingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const neededSlugsSet = new Set<string>();
    sections.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.autofill?.expression?.variables) {
          field.autofill.expression.variables.forEach((variable) => {
            if (variable.referenceType === 'data-reference-slug' && !isLoadingRef.current.has(variable.referenceId)) {
              neededSlugsSet.add(variable.referenceId);
            }
          });
        }
      });
    });

    additionalSlugs.forEach((slug) => {
      if (!isLoadingRef.current.has(slug)) {
        neededSlugsSet.add(slug);
      }
    });

    const slugsToLoad = Array.from(neededSlugsSet);

    if (slugsToLoad.length === 0) {
      return;
    }

    // Mark the slug as being loaded ouside of loadTheseSlugs so that it's handled synchronously.
    slugsToLoad.forEach((slug) => {
      isLoadingRef.current.add(slug);
    });

    const loadTheseSlugs = async (): Promise<void> => {
      const { data } = await getDatasets({ slugs: slugsToLoad });
      if (data) {
        setDatasets((prevState) => ({
          ...prevState,
          ...data.reduce(
            (acc, val) => {
              let thisDatasetData = val.data;
              try {
                thisDatasetData = typeof val.data === 'string' ? JSON.parse(val.data) : val.data;
              } catch (_error) {
                /* ignore */
              }

              // If this slug already exists in the accumulator, compare versions
              const existing = acc[val.slug];
              if (existing !== undefined) {
                // If both have versions, keep the one with the higher version
                const existingVersion = existing.version ?? -Infinity;
                const newVersion = val.version ?? -Infinity;

                if (newVersion > existingVersion) {
                  return {
                    ...acc,
                    [val.slug]: {
                      data: thisDatasetData,
                      version: val.version,
                    },
                  };
                }
                // Keep the existing one
                return acc;
              }

              // First occurrence of this slug
              return {
                ...acc,
                [val.slug]: {
                  data: thisDatasetData,
                  version: val.version,
                },
              };
            },
            {} as Record<string, DataSetWithVersion>,
          ),
        }));
      }
    };

    loadTheseSlugs();
  }, [sections, getDatasets, additionalSlugs]);

  // Extract just the data from each dataset (strip version wrapper)
  const dataSets = useMemo(
    () =>
      Object.keys(datasets).reduce(
        (acc, slug) => {
          acc[slug] = datasets[slug].data;
          return acc;
        },
        {} as Record<string, unknown>,
      ),
    [datasets],
  );
  return dataSets;
};

export default useExternalDataSets;
